import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getApiUrl } from '../config/api';

// Material UI
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Local
import Conversation from './Conversation';

import LinearProgress from '@mui/material/LinearProgress';

const MESSAGES_PER_PAGE = 100;

export default function Request() {
  const { requestId } = useParams();
  const [threads, setThreads] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [meta, setMeta] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const loadMoreRef = useRef(null);
  const lastLoadedRequestId = useRef(null);

  const loadMessages = useCallback(async (page = 1, append = false) => {
    if (page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await fetch(
        getApiUrl(`/v1/requests/messages/${requestId}?group=1&page=${page}&limit=${MESSAGES_PER_PAGE}`)
      );
      const results = await response.json();

      if (append) {
        // Use functional update to avoid depending on threads state
        setThreads(prevThreads => {
          if (!prevThreads) return results.threads;

          const mergedThreads = { ...prevThreads };
          Object.keys(results.threads).forEach(threadId => {
            if (mergedThreads[threadId]) {
              // Append messages to existing thread
              mergedThreads[threadId].messages = [
                ...mergedThreads[threadId].messages,
                ...results.threads[threadId].messages
              ];
            } else {
              // Add new thread
              mergedThreads[threadId] = results.threads[threadId];
            }
          });
          return mergedThreads;
        });
      } else {
        setThreads(results.threads);
      }

      setMeta(results.meta);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [requestId]);

  const loadMoreMessages = useCallback(() => {
    if (meta && meta.hasNext && !loadingMore) {
      loadMessages(currentPage + 1, true);
    }
  }, [meta, currentPage, loadingMore, loadMessages]);

  // Intersection Observer for automatic loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && meta && meta.hasNext && !loadingMore) {
          loadMoreMessages();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [loadMoreMessages, meta, loadingMore]);

  useEffect(() => {
    if (requestId && requestId !== lastLoadedRequestId.current) {
      lastLoadedRequestId.current = requestId;
      loadMessages(1, false);
    }
  }, [requestId, loadMessages]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
          Loading messages...
        </Typography>
      </Box>
    );
  }

  return (
    <Grid className="messages_root">
      {threads && Object.keys(threads).map((thread) => (
        <Card key={thread} sx={{ mb: 2 }}>
          <Conversation threadData={threads[thread]} name={thread} id={thread} />
        </Card>
      ))}

      {/* Load more section */}
      {meta && meta.hasNext && (
        <Box ref={loadMoreRef} sx={{ textAlign: 'center', p: 2 }}>
          {loadingMore ? (
            <>
              <LinearProgress sx={{ mb: 1 }} />
              <Typography variant="body2">Loading more messages...</Typography>
            </>
          ) : (
            <Button
              variant="outlined"
              onClick={loadMoreMessages}
              size="large"
            >
              Load More Messages ({meta.total - (currentPage * MESSAGES_PER_PAGE)} remaining)
            </Button>
          )}
        </Box>
      )}

      {/* Pagination info */}
      {meta && (
        <Box sx={{ textAlign: 'center', p: 2, color: 'text.secondary' }}>
          <Typography variant="caption">
            Showing {Math.min(currentPage * MESSAGES_PER_PAGE, meta.total)} of {meta.total} messages
          </Typography>
        </Box>
      )}
    </Grid>
  );
}

